import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getGuestToken,
  markReauthorizationRequired,
  refreshAccessToken,
  setAccessToken,
  setAccessTokenExpiry,
  setAuthStatus,
} from '../../redux/actions/auth/auth.actions';
import { selectAuthStatus } from '../../redux/selectors/auth.selector';
import store from '../../redux/store';
import { probeBrowserLiveness } from '../../services/webAPI/auth.service';
import {
  requestRefreshClaim,
  subscribeAuthEvents,
} from '../../services/webAPI/authChannel';
import { isInvalidOrRevokedCredentialError } from '../../services/webAPI/authError';
import {
  AUTH_STATUS_ANONYMOUS,
  AUTH_STATUS_AUTHENTICATED,
  AUTH_STATUS_BOOTSTRAPPING,
  AUTH_STATUS_REAUTHORIZATION_REQUIRED,
  GUEST_MINT_STATUS_IDLE,
  LOG_OUT,
} from '../../redux/types/user';

const revokeCurrentAvatar = () => {
  const avatar = store().getState()?.user?.avatar;
  if (typeof avatar === 'string' && avatar.length > 0) {
    URL.revokeObjectURL(avatar);
  }
};

/** Maximum transient auth recovery window. */
export const AUTH_RECOVERY_CEILING_MS = 30000;
/** Delay between transient auth recovery attempts. */
export const AUTH_RETRY_DELAY_MS = 5000;
/** Minimum interval between degraded liveness probes. */
export const AUTH_LIVENESS_INTERVAL_MS = 60000;
/** Refresh access token this long before expiry. */
export const AUTH_REFRESH_LEEWAY_MS = 60000;

/**
 * Reads JWT expiry without retaining or persisting token data.
 * @param {string} token browser access token
 * @returns {number}
 */
const getTokenExpiry = (token) => {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return 0;
    const payload = JSON.parse(
      atob(encodedPayload.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

/**
 * Bootstraps memory-only browser auth and coordinates recovery across tabs.
 * @returns {import('react').ReactElement}
 */
const AuthInitializer = () => {
  const dispatch = useDispatch();
  const authToken = useSelector(
    /** @type {(state: import('global').ReduxAppState) => string} */ (
      (state) => state.auth.authToken
    )
  );
  const authTokenExpiresAt = useSelector(
    /** @type {(state: import('global').ReduxAppState) => number} */ (
      (state) => state.auth.authTokenExpiresAt
    )
  );
  const authStatus = useSelector(selectAuthStatus);
  const guestToken = useSelector(
    /** @type {(state: import('global').ReduxAppState) => string} */ (
      (state) => state.auth.guestToken
    )
  );
  const guestMintStatus = useSelector(
    /** @type {(state: import('global').ReduxAppState) => string} */ (
      (state) => state.auth.guestMintStatus
    )
  );
  const mountedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const refreshWaitTimerRef = useRef(null);
  const livenessTimerRef = useRef(null);
  const refreshStartedAtRef = useRef(0);
  const lastLivenessProbeRef = useRef(0);
  const degradedRef = useRef(false);
  const runRefreshRef = useRef(null);
  const startLivenessRef = useRef(null);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (refreshWaitTimerRef.current) {
      clearTimeout(refreshWaitTimerRef.current);
      refreshWaitTimerRef.current = null;
    }
  }, []);

  const stopLiveness = useCallback(() => {
    if (livenessTimerRef.current) {
      clearInterval(livenessTimerRef.current);
      livenessTimerRef.current = null;
    }
  }, []);

  const resolveAnonymous = useCallback(
    async (keepDegraded = false) => {
      if (!mountedRef.current) return;
      clearRetry();
      if (!keepDegraded) {
        stopLiveness();
        degradedRef.current = false;
      }
      refreshStartedAtRef.current = 0;
      dispatch(setAuthStatus(AUTH_STATUS_ANONYMOUS));
      if (!guestToken) await dispatch(getGuestToken());
    },
    [clearRetry, dispatch, guestToken, stopLiveness]
  );

  const runRefresh = useCallback(
    (startedAt, isBootstrap) => {
      // Bootstrap may resolve anonymous; later refreshes stay silent so live UI remains mounted.
      const authState = store().getState()?.auth;
      const hadAuthenticatedSession = Boolean(authState?.authToken);
      const sessionEpoch = authState?.authSessionEpoch ?? 0;
      const tokenAtStart = authState?.authToken ?? '';
      const refreshPromise = isBootstrap
        ? Promise.resolve(dispatch(refreshAccessToken({ silent: false })))
        : requestRefreshClaim(sessionEpoch).then((ownsClaim) => {
            if (ownsClaim) {
              return dispatch(refreshAccessToken({ silent: true }));
            }
            refreshWaitTimerRef.current = setTimeout(() => {
              const currentAuth = store().getState()?.auth;
              if (
                mountedRef.current &&
                currentAuth?.authSessionEpoch === sessionEpoch &&
                currentAuth?.authToken === tokenAtStart
              ) {
                runRefreshRef.current?.(Date.now(), false);
              }
            }, AUTH_RETRY_DELAY_MS);
            return null;
          });
      refreshPromise
        .then((refreshResult) => {
          if (refreshResult === null) return;
          if (!mountedRef.current) return;
          const currentAuth = store().getState()?.auth;
          if (
            currentAuth?.authSessionEpoch !== sessionEpoch ||
            currentAuth?.status === AUTH_STATUS_ANONYMOUS ||
            currentAuth?.status === AUTH_STATUS_REAUTHORIZATION_REQUIRED
          ) {
            return;
          }
          clearRetry();
          degradedRef.current = false;
          stopLiveness();
          refreshStartedAtRef.current = 0;
          dispatch(setAuthStatus(AUTH_STATUS_AUTHENTICATED));
        })
        .catch((error) => {
          if (!mountedRef.current) return;
          if (error?.code === 'AUTH_REFRESH_INVALIDATED') return;
          if (isInvalidOrRevokedCredentialError(error)) {
            clearRetry();
            stopLiveness();
            degradedRef.current = false;
            refreshStartedAtRef.current = 0;
            if (isBootstrap && !hadAuthenticatedSession) {
              void resolveAnonymous();
            } else {
              revokeCurrentAvatar();
              dispatch(markReauthorizationRequired());
            }
            return;
          }

          // Transient failures retry for at most 30 seconds before entering degraded mode.
          if (Date.now() - startedAt < AUTH_RECOVERY_CEILING_MS) {
            dispatch(
              setAuthStatus(
                isBootstrap
                  ? AUTH_STATUS_BOOTSTRAPPING
                  : AUTH_STATUS_AUTHENTICATED
              )
            );
            retryTimerRef.current = setTimeout(
              () => runRefreshRef.current?.(startedAt, isBootstrap),
              AUTH_RETRY_DELAY_MS
            );
            return;
          }

          degradedRef.current = true;
          startLivenessRef.current?.();
          if (isBootstrap && !hadAuthenticatedSession) {
            void resolveAnonymous(true);
          }
        });
    },
    [clearRetry, dispatch, resolveAnonymous, stopLiveness]
  );

  runRefreshRef.current = runRefresh;

  const beginBootstrap = useCallback(() => {
    if (!mountedRef.current) return;
    clearRetry();
    stopLiveness();
    degradedRef.current = false;
    refreshStartedAtRef.current = Date.now();
    dispatch(setAuthStatus(AUTH_STATUS_BOOTSTRAPPING));
    runRefresh(Date.now(), true);
  }, [clearRetry, dispatch, runRefresh, stopLiveness]);

  const attemptLivenessProbe = useCallback(() => {
    if (
      !mountedRef.current ||
      !degradedRef.current ||
      Date.now() - lastLivenessProbeRef.current < AUTH_LIVENESS_INTERVAL_MS
    ) {
      return;
    }

    // Degraded recovery probes are capped at one per 60 seconds.
    lastLivenessProbeRef.current = Date.now();
    probeBrowserLiveness()
      .then(() => {
        if (!mountedRef.current) return;
        degradedRef.current = false;
        stopLiveness();
        const shouldBootstrap =
          store().getState()?.auth?.status !== AUTH_STATUS_AUTHENTICATED;
        if (shouldBootstrap) {
          beginBootstrap();
        } else {
          runRefresh(Date.now(), false);
        }
      })
      .catch(() => {});
  }, [beginBootstrap, runRefresh, stopLiveness]);

  const startLiveness = useCallback(() => {
    if (livenessTimerRef.current || !degradedRef.current) return;
    livenessTimerRef.current = setInterval(
      attemptLivenessProbe,
      AUTH_LIVENESS_INTERVAL_MS
    );
  }, [attemptLivenessProbe]);
  startLivenessRef.current = startLiveness;

  useEffect(() => {
    mountedRef.current = true;
    // Epoch checks reject stale token/logout messages from an older browser session or tab.
    const unsubscribe = subscribeAuthEvents((event) => {
      const currentEpoch = store().getState()?.auth?.authSessionEpoch ?? 0;
      if (
        event.type === 'access-token-refreshed' &&
        typeof event.accessToken === 'string' &&
        event.accessToken &&
        event.authSessionEpoch === currentEpoch
      ) {
        const accessToken =
          typeof event.accessToken === 'string' ? event.accessToken : '';
        const expiresAt =
          typeof event.expiresAt === 'number' ? event.expiresAt : 0;
        clearRetry();
        stopLiveness();
        degradedRef.current = false;
        refreshStartedAtRef.current = 0;
        dispatch(setAccessToken(accessToken));
        dispatch(setAccessTokenExpiry(expiresAt));
        dispatch(setAuthStatus(AUTH_STATUS_AUTHENTICATED));
      }
      if (event.type === 'reauthorization-required') {
        if (event.authSessionEpoch !== currentEpoch + 1) return;
        clearRetry();
        stopLiveness();
        degradedRef.current = false;
        refreshStartedAtRef.current = 0;
        revokeCurrentAvatar();
        dispatch(markReauthorizationRequired());
      }
      if (event.type === 'logged-out') {
        if (event.authSessionEpoch !== currentEpoch + 1) return;
        clearRetry();
        stopLiveness();
        degradedRef.current = false;
        refreshStartedAtRef.current = 0;
        revokeCurrentAvatar();
        dispatch({ type: LOG_OUT });
      }
    });

    // Network and focus recovery share the same liveness cadence and refresh path.
    const recover = () => {
      if (!degradedRef.current) return;
      attemptLivenessProbe();
    };
    window.addEventListener('online', recover);
    window.addEventListener('focus', recover);

    return () => {
      mountedRef.current = false;
      unsubscribe();
      clearRetry();
      stopLiveness();
      window.removeEventListener('online', recover);
      window.removeEventListener('focus', recover);
    };
  }, [attemptLivenessProbe, clearRetry, dispatch, stopLiveness]);

  useEffect(() => {
    if (authStatus !== AUTH_STATUS_BOOTSTRAPPING) return;
    if (authToken) {
      dispatch(setAuthStatus(AUTH_STATUS_AUTHENTICATED));
      return;
    }
    if (guestToken) {
      dispatch(setAuthStatus(AUTH_STATUS_ANONYMOUS));
      return;
    }
    if (!refreshStartedAtRef.current) beginBootstrap();
  }, [authStatus, authToken, beginBootstrap, dispatch, guestToken]);

  // Logout restores idle with empty tokens. Public routes stay on the skeleton
  // until this mint runs; failed and in-flight mints must not start another.
  useEffect(() => {
    if (authStatus !== AUTH_STATUS_ANONYMOUS) return;
    if (authToken || guestToken) return;
    if (guestMintStatus !== GUEST_MINT_STATUS_IDLE) return;
    dispatch(getGuestToken());
  }, [authStatus, authToken, dispatch, guestMintStatus, guestToken]);

  useEffect(() => {
    if (authStatus !== AUTH_STATUS_AUTHENTICATED || !authToken) return;
    const expiresAt = Number(authTokenExpiresAt) || getTokenExpiry(authToken);
    if (!expiresAt) return;
    const delay = Math.max(
      1000,
      expiresAt - Date.now() - AUTH_REFRESH_LEEWAY_MS
    );
    // Refresh proactively before expiry without changing the authenticated lifecycle state.
    const timer = setTimeout(() => {
      if (mountedRef.current && !degradedRef.current) {
        runRefresh(Date.now(), false);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [authStatus, authToken, authTokenExpiresAt, runRefresh]);

  useEffect(() => {
    if (degradedRef.current) startLiveness();
  }, [startLiveness, authStatus]);

  return <></>;
};

export default AuthInitializer;
